import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../svg-image/svg-image';
import '../../../design-system/button/button';
import { i18n } from '../../../i18n/i18n';
import resetStyle from '../html-content/reset.style';
import { CompletionBarEvents } from './completion-bar.model';
import { CoursePlayerState, stateContext } from '../../../entry-components/course-player/course-player.context';
import { consume } from '@lit/context';

export type CompletionBarVariant = 'congratulations' | 'unsuccessful';

@customElement('lua-completion-bar')
export class LuaCompletionBar extends LitElement {
  static override styles = [
    resetStyle,
    css`
      :host {
        display: flex;
        align-items: center;
        background-color: var(--semantic-colour-tokens-surface-surface-secondary);
        border-radius: var(--corners-m);
        padding: var(--spacing-spacing-08) var(--spacing-spacing-16);
        width: 100%;
        justify-content: space-between;
        margin: var(--spacing-spacing-12) 0;
        max-width: max-content;
        gap: var(--spacing-spacing-12);
      }
      p {
        margin: 0;
        padding: 0;
        color: var(--semantic-colour-tokens-text-text-primary);
        font-weight: 600;
        text-align: left;
        flex-grow: 1;
        font-size: 16px;
      }
    `,
  ];

  @consume({ context: stateContext, subscribe: true })
  private coursePlayerState: CoursePlayerState;

  @property({ type: String })
  variant: CompletionBarVariant = 'congratulations';

  /**
   * Checks if there are any actionable completion items
   */
  private hasCompletionActions(): boolean {
    const items = this.coursePlayerState.getCompletionItems?.() || [];
    return items.some((item) => item.buttons && item.buttons.length > 0);
  }

  /**
   * Determines if the "View options" button should be shown
   */
  private shouldShowViewOptionsButton(): boolean {
    // showExitCourseButton is false when embedded_content is true
    // If not embedded, always show the button (user can exit course)
    if (this.coursePlayerState.showExitCourseButton) {
      return true;
    }

    // For embedded courses, only show if there are completion actions
    return this.hasCompletionActions();
  }

  seeMoreHandler() {
    this.dispatchEvent(new CustomEvent(CompletionBarEvents.SEE_MORE_CLICKED, { bubbles: true, composed: true }));
  }

  override render() {
    const imageMap: Record<CompletionBarVariant, string> = {
      congratulations: 'congratulations',
      unsuccessful: 'trouble',
    };

    return html`
      <lua-svg-image .name="${imageMap[this.variant]}" width="48" height="48"></lua-svg-image>

      <p><slot></slot></p>
      ${this.shouldShowViewOptionsButton()
        ? html`<luds-button
            @click=${this.seeMoreHandler}
            size="extra-small"
            variant="secondary"
            data-qa-id="View options"
            >${i18n('lua.courses.completion_bar.see_more')}</luds-button
          >`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lua-completion-bar': LuaCompletionBar;
  }

  interface HTMLElementEventMap {
    [CompletionBarEvents.SEE_MORE_CLICKED]: CustomEvent;
  }
}
